import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Card, Col, Form, Row, App, Typography, Table } from 'antd';
import { LeftOutlined } from '@ant-design/icons';
import { getEvaluationById, rateVideo } from '../../services/evaluations';
import { InputNumber } from 'antd';
import { resolveMedia } from '../../utils/media';
import useAuth from '../../hooks/useAuth';
import GuiaPDF from '../../assets/Guía de Puntuación Videos.pdf';
import usePermissions from '../../hooks/usePermissions';
import { Divider } from 'antd';

const { Title } = Typography;

const { useApp } = App;

const RateVideoPage = () => {
  const { id } = useParams();
  const [isEdit, setIsEdit] = useState(false);
  
  const navigate = useNavigate();
  const { message } = useApp();
  const [form] = Form.useForm();

  const [loading, setLoading] = useState(false);
  const [evaluation, setEvaluation] = useState([]);
  const [scores, setScores] = useState({});
  const { user } = useAuth();
  const { can } = usePermissions();

  const columns = [
    {
        title: 'Criterio',
        dataIndex: 'description',
        render: (_, record) => ( record.criteria?.description )
    },
    {
        title: 'Puntuación máxima',
        dataIndex: 'score',
        align: 'center',
        render: (_, record) => {
        const isTotal = record.isTotal;
        return (
          <span
            style={{
              color: isTotal ? '#3F8600' : 'inherit',
              fontWeight: isTotal ? 600 : 400,
            }}
          >
            {_}%
          </span>
        );
      },
    },
    {
      title: 'Puntuación IA',
      dataIndex: 'aiScore',
      align: 'center',
      render: (_, record) => {
        if (record.isTotal) {
          const totalMax = record.score || 0;
          const totalBase10 =
            totalMax > 0 ? ((record.aiScore || 0) / totalMax) * 10 : 0;

          return totalBase10.toFixed(3);
        }

        const base10 =
          record.score > 0
            ? ((record.aiScore || 0) / record.score) * 10
            : 0;

        return base10.toFixed(3);
      }
    },
    {
      title: 'Puntuación video',
      align: 'center',
      render: (_, record) => {
        const isTotal = record.isTotal || false;

        if (isTotal) {
          const totalJudgeScore = Object.values(scores).reduce(
            (acc, value) => acc + (Number(value) || 0),
            0
          );

          const totalMax = record.score || 0;

          const totalBase10 =
            totalMax > 0 ? (totalJudgeScore / totalMax) * 10 : 0;

          return (
            <span>
              {totalBase10.toFixed(3)} / 10
            </span>
          );
        }

        const realValue = scores[record.criteria.id] || 0;

        const base10Value =
          record.score > 0
            ? (realValue / record.score) * 10
            : 0;

        return (
          <InputNumber
            min={0}
            max={10}
            step={1}
            value={base10Value}
            disabled={!isEdit}
            onChange={(value) => {
              const realScore =
                record.score > 0
                  ? ((value ?? 0) / 10) * record.score
                  : 0;

              setScores(prev => ({
                ...prev,
                [record.criteria.id]: realScore,
              }));
            }}
          />
        );
      },
    },
    ];
  useEffect(() => {
    if (!id) return;

    setLoading(true);
    getEvaluationById(id)
      .then((evaluation) => {
        const judgeEvaluation = evaluation.averageScore ??  evaluation.evaluations?.filter(evaluation => evaluation.judgeId === user?.id)[0]; // un solo juez
        setIsEdit(evaluation.status !== 'FINALIZED' && judgeEvaluation?.status !== 'SUBMITTED');

        
        const criteriaScores = judgeEvaluation?.criteriaScores || [];

        const initialScores = {};

        evaluation?.competition?.criteria?.forEach(c => {
          const foundScore = criteriaScores.find(
            cs => cs.criteriaId === c.criteriaId
          );

          initialScores[c.criteriaId] = foundScore?.score ?? 0;
        });

        setScores(initialScores);

        const criteriaWithAIScore = (evaluation?.competition?.criteria || []).map(c => {
          let aiScore = 0;
          switch (c.criteria?.name || c.criteria?.description) {
            case 'Dificultad/Técnica':
              aiScore = evaluation?.aiTechnicalScore;
              break;
            case 'Importancia/Contexto':
              aiScore = evaluation?.aiImportanceScore;
              break;
            case 'Creatividad/Originalidad':
              aiScore = evaluation?.aiCreativityScore;
              break;
            case 'Impacto visual/Presentación':
              aiScore = evaluation?.aiVisualScore;
              break;
            case 'Legitimidad/Conducta':
              aiScore = evaluation?.aiLegitimacyScore;
              break;
            default:
              aiScore = 0;
          }
          aiScore = (aiScore / 10) * c.score
          return {
            ...c,
            aiScore,
          };
        });

        const totalMax = criteriaWithAIScore.reduce(
          (acc, item) => acc + (item.score || 0),
          0
        );

        const totalAI = criteriaWithAIScore.reduce(
          (acc, item) => acc + (item.aiScore || 0),
          0
        );

        const totalJudgeScore = Object.values(initialScores).reduce(
          (acc, value) => acc + (value || 0),
          0
        );

        criteriaWithAIScore.push({
          id: 'total',
          criteria: { description: 'Total' },
          score: totalMax,
          aiScore: totalAI,
          judgeScore: totalJudgeScore,
          isTotal: true,
        });
        
        const evaluationAI = {
          ...evaluation,
          competition: {
            ...(evaluation.competition || {}),
            criteria: criteriaWithAIScore,
          },
        };

        setEvaluation(evaluationAI);

      })
      .catch(() => {
        message.error('No se pudo cargar la evaluación');
      })
      .finally(() => {
        setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, form]);

  /**
   * Función que se ejecuta cuando se envía el formulario
   */
  const onSubmit = async () => {
        try {
            setLoading(true);

            const payload = {
              judgeId: user.id,
              status: 'SUBMITTED',  
              scores: Object.entries(scores).map(([criteriaId, score]) => ({
                  criteriaId: Number(criteriaId),
                  score,
              })),
            };

            await rateVideo(id, payload);
            message.success('Evaluación guardada correctamente');
            navigate('/evaluations/all');
        } catch (error) {
            message.error('No se pudo guardar la evaluación');
        } finally {
            setLoading(false);
        }
    };


  const handleBack = () => {
    navigate('/evaluations/all');
  };

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Button 
            type="text" 
            icon={<LeftOutlined style={{ fontSize: '24px', strokeWidth: 4 }} />} 
            style={{ 
              padding: 0,
              color: '#BA955E',
              fontSize: '24px',
              fontWeight: '700',
              height: 'auto'
            }}
            onClick={handleBack}
          />
        <Title level={3} style={{ marginBottom: 0 }}>
          {isEdit ? 'Calificar video' : 'Ver video'}
        </Title>
      </div>
      <Card loading={loading}>
        <Title level={3} style={{ marginBottom: 0 }}>
            Video a evaluar: {evaluation?.competition?.modality?.description}{' '}
            {evaluation?.competition?.regions?.length > 0
            ? evaluation.competition.regions
                .map(r => r.region?.description)
                .filter(Boolean)
                .join(', ')
            : evaluation?.region?.description || '-'}{' '}
            {evaluation?.competition?.description}
        </Title>
        <Form form={form} onFinish={onSubmit}>
            <Row gutter={24}>
              <Col xs={24} md={10}>
                <video
                  src={resolveMedia(evaluation?.urlVideo)}
                  controls
                  style={{ width: '100%', borderRadius: 8 }}
                />
    
                <div style={{ marginTop: 16 }}>
                  <strong>{evaluation?.folio || '—'}</strong>
                  <br />
                  Usuario: {evaluation?.playerName || '—'}
                </div>
                <Row style={{ marginTop: 32 }}>
                  <Col xs={24}>
                    <Table
                        rowKey="id"
                        dataSource={evaluation?.competition?.criteria || []}
                        columns={columns}
                        pagination={false}
                        scroll={{ x: 'max-content' }}
                    />
                  </Col>
                </Row>
              </Col>
              <Col xs={24} md={14}>
                  <Title
                    level={5}
                    style={{
                      color: '#BA955E',
                      marginTop: 24,
                      marginBottom: 16,
                      fontWeight: 600,
                    }}
                  >
                    Título del video
                  </Title>
      
                  <div style={{ borderBottom: '1px solid #E5E5E5', paddingBottom: 12, marginBottom: 12 }}>
                    <div style={{ textAlign: 'justify' }}>
                      {evaluation?.title || '—'}
                    </div>
                  </div>
      
                  <div style={{ borderBottom: '1px solid #E5E5E5', paddingBottom: 12, marginBottom: 12 }}>
                    <strong>Descripción:</strong>
                    <div style={{ textAlign: 'justify' }}>
                      {evaluation?.description || '—'}
                    </div>
                  </div>
      
                  <br /><br /><br />
                  <Col xs={24} md={14}>
                    <a
                      href={GuiaPDF}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: '#BA955E' }}
                    >
                      Consultar criterios de evaluación (PDF)
                    </a>
                  </Col>
                </Col>
                <Divider/>
                
            </Row>

            <Row justify="end" gutter={12} style={{ marginTop: 24 }}>
                <Col>
                <Button onClick={() => navigate(-1)} disabled={loading}>
                    Cancelar
                </Button>
                </Col>
                {can('evaluation.evaluate') && (<Col>
                  <Button type="primary" htmlType="submit" loading={loading} disabled={!isEdit}>
                      {isEdit ? 'Guardar cambios' : 'Guardar'}
                  </Button>
                </Col>)}
            </Row>
        </Form>
      </Card>
    </div>
  );
};

export default RateVideoPage;

